import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import type { CharacterView } from '@pk/shared';
import { AGE_OF_MAJORITY, SQUIRE_XP_MAX } from '@pk/shared';
import { useCharacter, useHouse, useTitle, useTitleIndex, useWorld } from '../api/hooks';
import { Collapsible, Empty, ErrorBox, Loading, Screen, ScreenTop, ScrollArea, Section, Segmented, Tag } from '../components/ui';
import { CharLink, CharPortrait, TraitTags } from '../components/game';
import { IconTree, IconUsers } from '../components/icons';
import { busyMembers, memberCan } from '../lib/houseActions';
import type { MemberAct } from '../lib/houseActions';
import '../styles/family.css';

const ACT_PARAM: Record<MemberAct, string> = { edu: 'edu', marry: 'marry', service: 'service', petition: 'petition', squire: 'squire' };
/** The one or two most relevant quick actions per member, in priority order. */
const ACT_ORDER: MemberAct[] = ['edu', 'marry', 'squire', 'petition', 'service'];

function MasterTag({ id }: { id: string }) {
  const { t } = useTranslation();
  const { data } = useCharacter(id);
  return <Tag tone="blue">{t('chivalry.master')}: {data?.firstName ?? '…'}</Tag>;
}

type Filter = 'all' | 'action' | 'chivalry';
const FILTERS: Filter[] = ['all', 'action', 'chivalry'];
const inChivalry = (m: CharacterView) => m.dubbed || m.squireXp !== null || !!m.knightOf || !!m.squireOf || !!m.guardOf || !!m.guardCaptainOf;

export function FamilyScreen() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const rawFilter = params.get('filter');
  const filter: Filter = FILTERS.includes(rawFilter as Filter) ? (rawFilter as Filter) : 'all';
  const setFilter = (f: Filter) => {
    const next = new URLSearchParams(params);
    if (f === 'all') next.delete('filter'); else next.set('filter', f);
    setParams(next, { replace: true });
  };
  const house = useHouse();
  const world = useWorld();
  const titles = useTitleIndex();
  const headTitleId = house.data?.members.find((m) => m.isHead)?.primaryTitleId;
  const headTitle = useTitle(headTitleId);
  const heirId = headTitle.data?.heirId ?? null;
  if (house.isLoading || world.isLoading) return <Loading />;
  if (house.isError || world.isError) return <ErrorBox error={house.error ?? world.error} onRetry={() => { void house.refetch(); void world.refetch(); }} />;
  const h = house.data!;
  const w = world.data!;
  const byId = new Map(h.members.map((m) => [m.id, m]));
  const living = h.members.filter((m) => m.alive);
  const dead = h.members.filter((m) => !m.alive);
  const busy = busyMembers(h);

  const head = living.find((m) => m.isHead);
  const spouse = head?.spouseId ? byId.get(head.spouseId) : undefined;
  const core = new Set([head?.id, spouse?.id].filter(Boolean) as string[]);
  const rest = living.filter((m) => !core.has(m.id));
  // Children of the head (or the head's spouse), whatever their age; everyone else is a further relative.
  const kin = new Set([...(head?.childIds ?? []), ...(spouse?.childIds ?? [])]);
  const isChild = (m: CharacterView) => kin.has(m.id) || (!!m.fatherId && core.has(m.fatherId)) || (!!m.motherId && core.has(m.motherId));
  // Day-to-day order: whoever has something to do comes first, then oldest first.
  const todoCount = (m: CharacterView) => ACT_ORDER.filter((a) => memberCan(m, h, w, busy)[a]).length;
  const needsAction = new Set(living.filter((m) => todoCount(m) > 0).map((m) => m.id));
  const byUse = (a: CharacterView, b: CharacterView) => Number(needsAction.has(b.id)) - Number(needsAction.has(a.id)) || b.age - a.age;
  const children = rest.filter(isChild).sort(byUse);
  const others = rest.filter((m) => !isChild(m)).sort(byUse);
  const filtered = filter === 'all' ? [] : living
    .filter((m) => (filter === 'action' ? needsAction.has(m.id) : inChivalry(m)))
    // chivalry: unplaced squires first, then knights, then the rest
    .sort((a, b) => (filter === 'chivalry'
      ? Number(b.squireXp !== null && !b.squireOf) - Number(a.squireXp !== null && !a.squireOf) || Number(b.dubbed) - Number(a.dubbed)
      : 0) || b.age - a.age);

  const card = (m: CharacterView) => {
    const can = memberCan(m, h, w, busy);
    const acts = ACT_ORDER.filter((a) => can[a]).slice(0, 2);
    const served = m.knightOf ? titles.get(m.knightOf) : undefined;
    const squired = m.squireOf ? titles.get(m.squireOf) : undefined;
    const partner = m.spouseId ?? m.betrothedId;
    return (
      <article key={m.id} className={`fam-card ${m.isHead ? 'head' : ''}`}>
        <Link to={`/character/${m.id}`} className="fam-main">
          <CharPortrait c={m} size={64} />
          <div className="fam-body">
            <div className="fam-name">{m.titledName ?? m.name}</div>
            <div className="muted small">{t('common.age', { age: m.age })}{m.primaryTitle && !served ? ` · ${m.primaryTitle}` : ''}</div>
            <div className="fam-tags">
              {m.isHead && <Tag tone="gold">{t('character.headShort')}</Tag>}
              {heirId === m.id && <Tag tone="blue">{t('family.heir')}</Tag>}
              {spouse?.id === m.id && <Tag>{t('family.spouse')}</Tag>}
              {served && <Tag tone="blue">{t('character.knightOf')} {served.name}</Tag>}
              {m.newHouse && <Tag tone="green" title={t('newHouse.hint')}>{t('newHouse.badge')}</Tag>}
              {m.dubbed && <Tag tone="gold">{t(`chivalry.tier.${m.knightTier ?? 'bachelor'}`)}</Tag>}
              {m.squireXp !== null && <Tag tone="green" title={`${m.squireXp} / ${SQUIRE_XP_MAX}`}>{t(`chivalry.rank.${m.squireRank ?? 'novice'}`)} {m.squireXp}%</Tag>}
              {m.squireMasterId && <MasterTag id={m.squireMasterId} />}
              {m.guardOf && <Tag tone="blue">{t('chivalry.guard')} {titles.get(m.guardOf)?.name ?? ''}</Tag>}
              {m.squireXp !== null && !m.squireOf && <Tag>{t('chivalry.unplaced')}</Tag>}
              {m.guardCaptainOf && <Tag tone="gold">{t('chivalry.captain')} {titles.get(m.guardCaptainOf)?.name ?? ''}</Tag>}
              {squired && !m.squireMasterId && !m.guardOf && <Tag tone="blue">{t('character.squireOf')} {squired.name}</Tag>}
              {m.education && <Tag tone="green">{t('family.inEducation')}: {t(`attr.${m.education.focus}`)}</Tag>}
              {!m.spouseId && !m.betrothedId && m.age >= AGE_OF_MAJORITY && !m.isHead && <Tag>{t('family.unmarried')}</Tag>}
              {m.prisonerOf && <Tag tone="red">{t('character.prisoner')}</Tag>}
            </div>
            <TraitTags traits={m.traits} max={2} hideEducation />
          </div>
        </Link>
        {partner && (
          <div className="small muted fam-partner">{m.spouseId ? t('family.marriedTo') : t('family.betrothedTo')} <CharLink id={partner} /></div>
        )}
        {acts.length > 0 && (
          <div className="fam-acts">
            {acts.map((a) => <Link key={a} to={`/character/${m.id}?act=${ACT_PARAM[a]}`} className="btn small">{t(`family.act.${a}`)}</Link>)}
          </div>
        )}
      </article>
    );
  };

  return (
    <Screen className="fam-page">
      <ScreenTop>
      <div className="fam-bar">
        <span className="fam-count"><IconUsers />{t('family.subtitle', { count: living.length })}</span>
        <Link to={`/tree/${h.house.id}`} className="btn small primary fam-tree-btn"><IconTree />{t('house.tree')}</Link>
      </div>
      <Segmented<Filter> value={filter} onChange={setFilter} options={FILTERS.map((f) => ({
        value: f,
        label: <>{t(`famFilter.${f}`)}{f === 'action' && needsAction.size > 0 ? <span className="num fam-filter-n">{needsAction.size}</span> : null}</>,
      }))} />
      </ScreenTop>
      <ScrollArea>
      {filter !== 'all' && (filtered.length === 0 ? <Empty>{t('famFilter.none')}</Empty> : <div className="fam-grid">{filtered.map(card)}</div>)}
      {filter === 'all' && (head || spouse) && (
        <Section title={t('family.headSpouse')}>
          <div className="fam-grid">{head && card(head)}{spouse && card(spouse)}</div>
        </Section>
      )}
      {filter === 'all' && children.length > 0 && (
        <Section title={t('family.children')}>
          <div className="fam-grid">{children.map(card)}</div>
        </Section>
      )}
      {filter === 'all' && others.length > 0 && (
        <Section title={t('family.others')}>
          <div className="fam-grid">{others.map(card)}</div>
        </Section>
      )}
      {dead.length > 0 && (
        <div className="panel fam-departed">
          <Collapsible title={t('family.departed')} summary={dead.length}>
            <div className="fam-grid">
              {dead.map((m) => (
                <Link key={m.id} to={`/character/${m.id}`} className="fam-dead">
                  <CharPortrait c={m} size={40} />
                  <span><b>{m.name}</b><span className="muted small">{t('character.diedAt', { age: m.age })}</span></span>
                </Link>
              ))}
            </div>
          </Collapsible>
        </div>
      )}
      </ScrollArea>
    </Screen>
  );
}
